import {
  ApiRequest,
  GetBaseServiceFunctions,
  queryClient,
} from '../baseService'

const BaseRoute = 'app/companies'

const baseFunctions = GetBaseServiceFunctions(BaseRoute)

function Enable(id) {
  queryClient.invalidateQueries([BaseRoute, id])
  return ApiRequest.put(`${BaseRoute}/${id}/enable`)
}

function Disable(id) {
  queryClient.invalidateQueries([BaseRoute, id])
  return ApiRequest.put(`${BaseRoute}/${id}/disable`)
}

function GetBusinessUnits(companyId, params = {}) {
  return ApiRequest.get(`${BaseRoute}/${companyId}/businessUnits`, {
    params,
  })
}

function GetBookingHistory(params = {}) {
  return ApiRequest.get(`${BaseRoute}/bookingHistory`, {
    params,
  })
}

function GetServiceBookingHistory(params = {}) {
  return ApiRequest.get(`${BaseRoute}/serviceBookingHistory`, {
    params,
  })
}

function CreateBusinessUnit(companyId, data) {
  queryClient.invalidateQueries([BaseRoute, companyId])
  return ApiRequest.post(`${BaseRoute}/${companyId}/businessUnits`, data)
}

function UpdateBusinessUnit(companyId, businessUnitId, data) {
  queryClient.invalidateQueries([BaseRoute, companyId])
  return ApiRequest.put(
    `${BaseRoute}/${companyId}/businessUnits/${businessUnitId}`,
    data
  )
}

function DeleteBusinessUnit(companyId, businessUnitId) {
  queryClient.invalidateQueries([BaseRoute, companyId])
  return ApiRequest.delete(
    `${BaseRoute}/${companyId}/businessUnits/${businessUnitId}`
  )
}

function CheckDuplicate(params) {
  return ApiRequest.get(`${BaseRoute}/isDupplicateName`, { params })
}

function GetSimilarItemsByName(params) {
  return ApiRequest.get(`${BaseRoute}/similarItemsByName`, { params })
}

function GetFavouriteSpecialists(id, params) {
  return ApiRequest.get(`${BaseRoute}/${id}/favourite-specialists`, { params })
}

function AddFavouriteSpecialists(id, specialistId) {
  queryClient.invalidateQueries([BaseRoute, id])
  return ApiRequest.post(`${BaseRoute}/${id}/favourite-specialists`, {
    specialistId,
  })
}
function DeleteFavouriteSpecialists(id, specialistId) {
  queryClient.invalidateQueries([BaseRoute, id])
  return ApiRequest.delete(
    `${BaseRoute}/${id}/favourite-specialists/${specialistId}`
  )
}
function ExportSearchResult(payload = {}) {
  return ApiRequest.post(`${BaseRoute}/export`, payload, {
    responseType: 'arraybuffer',
  })
}
function DownloadUploadCompanyTemplate() {
  return ApiRequest.post(
    `${BaseRoute}/template-import`,
    {},
    { responseType: 'arraybuffer' }
  )
}
function UploadFiles(file) {
  return ApiRequest.post(`${BaseRoute}/import`, file)
}

function CompanyPMSConnect(companyPMSId) {
  return ApiRequest.get(`${BaseRoute}/${companyPMSId}/pms-config-authorize`)
}

function CompanyPMSDisconnect(companyPMSId) {
  return ApiRequest.get(`${BaseRoute}/${companyPMSId}/pms-config-deauthorize`)
}

function CompanyPMSLogoutUrl(companyPMSId) {
  return ApiRequest.get(`${BaseRoute}/${companyPMSId}/pms-config-logout-url`)
}

function GetPMSConfig(companyId) {
  return ApiRequest.get(`${BaseRoute}/${companyId}/pms-config`)
}
function SavePMSConfig(companyId, data) {
  queryClient.invalidateQueries([BaseRoute, companyId])
  return ApiRequest.post(`${BaseRoute}/${companyId}/pms-config`, data)
}

function UpdateSyncMatterNo(payload) {
  const params = new URLSearchParams({
    thirdPartySource: payload.thirdPartySource,
    matterNo: payload.matterNo,
    remove: (payload.remove ?? false).toString(),
  })
  return ApiRequest.post(
    `${BaseRoute}/${payload.companyPMSId}/pms-matter-sync?${params.toString()}`
  )
}

function PMSSendRequest(companyPMSId, data) {
  queryClient.invalidateQueries([BaseRoute, companyPMSId])
  return ApiRequest.post(`${BaseRoute}/${companyPMSId}/pms-send-request`, data)
}

function GetBookingAgents(companyId) {
  return ApiRequest.get(`${BaseRoute}/${companyId}/booking-agents`)
}

function UpdateBookingAgents(companyId, data) {
  return ApiRequest.put(`${BaseRoute}/${companyId}/booking-agents`, data)
}

function NotifyProviderReportMonthly(companyId) {
  return ApiRequest.post(
    `${BaseRoute}/${companyId}/notify-provider-report-monthly`
  )
}

function GetDiscounts(companyId: string) {
  return ApiRequest.get(`${BaseRoute}/${companyId}/discounts`)
}

function CreateDiscount(companyId: string, data: any) {
  queryClient.invalidateQueries([BaseRoute, companyId, 'discounts'])
  return ApiRequest.post(`${BaseRoute}/${companyId}/discounts`, data)
}

function UpdateDiscount(companyId: string, discountId: string, data: any) {
  queryClient.invalidateQueries([BaseRoute, companyId, 'discounts'])
  return ApiRequest.put(
    `${BaseRoute}/${companyId}/discounts/${discountId}`,
    data
  )
}

function DeleteDiscount(companyId: string, discountId: string) {
  queryClient.invalidateQueries([BaseRoute, companyId, 'discounts'])
  return ApiRequest.delete(`${BaseRoute}/${companyId}/discounts/${discountId}`)
}

function GetCustomizableEmailTemplateKeys() {
  return ApiRequest.get(`${BaseRoute}/customizableEmailTemplateKeys`)
}

function GetCustomEmailTemplates(companyId: string) {
  return ApiRequest.get(`${BaseRoute}/${companyId}/customEmailTemplates`)
}

function CreateCustomEmailTemplate(companyId: string, data: any) {
  queryClient.invalidateQueries([BaseRoute, companyId, 'customEmailTemplates'])
  return ApiRequest.post(`${BaseRoute}/${companyId}/customEmailTemplates`, data)
}

function UpdateCustomEmailTemplate(
  companyId: string,
  templateId: string,
  data: any
) {
  queryClient.invalidateQueries([BaseRoute, companyId, 'customEmailTemplates'])
  return ApiRequest.put(
    `${BaseRoute}/${companyId}/customEmailTemplates/${templateId}`,
    data
  )
}

function DeleteCustomEmailTemplate(companyId: string, templateId: string) {
  queryClient.invalidateQueries([BaseRoute, companyId, 'customEmailTemplates'])
  return ApiRequest.delete(
    `${BaseRoute}/${companyId}/customEmailTemplates/${templateId}`
  )
}

const API = {
  ...baseFunctions,
  Enable,
  Disable,
  GetBusinessUnits,
  CreateBusinessUnit,
  UpdateBusinessUnit,
  DeleteBusinessUnit,
  GetBookingHistory,
  getSimilarItems: CheckDuplicate,
  GetFavouriteSpecialists,
  AddFavouriteSpecialists,
  DeleteFavouriteSpecialists,
  GetSimilarItemsByName,
  ExportSearchResult,
  DownloadUploadCompanyTemplate,
  UploadFiles,
  GetPMSConfig,
  SavePMSConfig,
  CompanyPMSConnect,
  CompanyPMSDisconnect,
  CompanyPMSLogoutUrl,
  PMSSendRequest,
  GetBookingAgents,
  GetServiceBookingHistory,
  UpdateBookingAgents,
  NotifyProviderReportMonthly,
  UpdateSyncMatterNo,
  GetDiscounts,
  CreateDiscount,
  UpdateDiscount,
  DeleteDiscount,
  GetCustomizableEmailTemplateKeys,
  GetCustomEmailTemplates,
  CreateCustomEmailTemplate,
  UpdateCustomEmailTemplate,
  DeleteCustomEmailTemplate,
}

export default API
